import store from '@/store/index.js'
import i18n from '@/i18n'
import { speciesLayerEnabled } from '@/utils/dueDiligence/mode.js'
import { computeSpeciesOverlap, aggregateHitsByGroup } from '@/utils/dueDiligence/speciesOverlap.js'

const notSeeded = () => ({ seeded: false, itemCount: 0, warnings: [] })

function snack (key) {
  store.dispatch('snackbar/setSnack', i18n.t(key))
}

async function hasMitigationItems (tractId) {
  try {
    const items = await store.dispatch('tract/fetchTractMitigationItems', tractId)
    return items.length > 0
  } catch (e) {
    console.error(e)
    return false
  }
}

// Zero seeded items leave the tract unexempted unless an exemption is flagged to auto-assign
async function lacksAutoAssignExemption () {
  try {
    const exemptions = await store.dispatch('due-diligence-exemption/fetchDueDiligenceExemptions')
    return !exemptions.some(exemption => exemption.autoAssignWhenNoHits)
  } catch (e) {
    console.error(e)
    return false
  }
}

// Seed-only: the server refuses to replace existing items, so this never runs on a tract that has any.
// Every message worth showing is snacked here; the result lets callers react to the outcome.
export async function seedMitigationItems (tractId, cutFeatureCollection) {
  if (!speciesLayerEnabled()) return notSeeded()
  if (!(cutFeatureCollection?.features?.length > 0)) return notSeeded()
  if (await hasMitigationItems(tractId)) return notSeeded()
  let hits
  try {
    hits = await computeSpeciesOverlap(cutFeatureCollection)
  } catch (e) {
    if (e.code === 'OVERLAP_AREA_TOO_LARGE') {
      snack('speciesOverlapAreaTooLarge')
      return notSeeded()
    }
    if (e.code === 'OVERLAP_FETCH_FAILED') {
      snack('speciesOverlapFetchFailed')
      return notSeeded()
    }
    throw e
  }
  // Every group must resolve before anything is sent: the server rejects an unknown group and
  // refuses species on a group that does not require habitats, so a missed group poisons the payload.
  let groupsById
  try {
    const groups = await store.dispatch('mitigation-group/fetchMitigationGroups')
    groupsById = new Map(groups.map(g => [g.mitigationGroupId, g]))
  } catch (e) {
    console.error(e)
    snack('speciesOverlapGroupsUnavailable')
    return notSeeded()
  }
  const { entries, droppedSpeciesGroupIds, unresolvedGroupHits } = aggregateHitsByGroup(hits, groupsById)
  if (unresolvedGroupHits > 0) {
    snack('speciesOverlapUnmatchedGroups')
    return notSeeded()
  }
  const warnings = []
  if (droppedSpeciesGroupIds.length > 0) {
    warnings.push(i18n.t('speciesDroppedForNonSpeciesGroup'))
  }
  let seededItems
  try {
    seededItems = await store.dispatch('tract/updateAllTractMitigationItems', { tractId, mitigationItems: entries })
  } catch (e) {
    console.error(e)
    return notSeeded()
  }
  if (seededItems.length === 0 && await lacksAutoAssignExemption()) {
    warnings.push(i18n.t('speciesOverlapNoAutoAssignExemption'))
  }
  if (warnings.length > 0) store.dispatch('snackbar/setSnack', warnings.join(' '))
  return { seeded: true, itemCount: seededItems.length, warnings }
}
